import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { toast } from 'react-toastify';
import MediaContentCreator from "./CreateMediaPlanContent";
import BlankData from "../../components/common/ui/BlankData";
import { jobService } from "../../services/job.service";
import PromptInput from "../../components/common/ui/PromptInput";
import { Button, Card, CardHeader, Icon, StatusPill, Tabs } from "../../ds";
import { Loading, Note } from "../../components/page";

// Fix for SocialMediaPost component in MediaPlanContent.jsx

const SocialMediaPost = ({
    platform,
    icon,
    content,
    isGenerating,
    onGenerate,
    onSave,
    threadId,
    assistantId,
    setMediaContent,
    fetchMediaContent
}) => {
    const [inputContent, setInputContent] = useState("");
    const [isSaving, setIsSaving] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false);
    const [displayContent, setDisplayContent] = useState(content); // Track display content separately
    const [activeTab, setActiveTab] = useState(0); // Track active tab for multiple options

    // Update displayContent when content prop changes
    useEffect(() => {
        console.log('SocialMediaPost received content:', content);
        console.log('Content type:', typeof content);
        console.log('Content has options:', content && typeof content === 'object' && content.options);
        setDisplayContent(content);
    }, [content]);

    const handleGenerate = async () => {
        if (!inputContent.trim()) return;
        setIsUpdating(true);
        try {
            // Extract job ID from URL — supports /jobs/:id/... and /job/:id/...
            const parts = window.location.pathname.split('/').filter(Boolean);
            const jobIdx = parts.findIndex(p => p === 'jobs' || p === 'job');
            const jobId = jobIdx >= 0 ? parts[jobIdx + 1] : parts[1];

            const response = await jobService.continueMediaPlan(
                jobId,
                platform.id.toLowerCase(),
                {
                    user_message: inputContent,
                    existing_content: typeof (displayContent || content) === 'string'
                        ? (displayContent || content)
                        : JSON.stringify(displayContent || content),
                }
            );

            console.log('[MediaPlan enhance] raw response:', response);

            // Extract content from various possible response shapes
            const newContent =
                response?.data?.content ??
                response?.data?.data?.content ??
                response?.content ??
                null;

            console.log('[MediaPlan enhance] newContent:', newContent);

            if (newContent) {
                // Update local display immediately
                setDisplayContent(newContent);
                // Update parent state so the prop stays in sync
                setMediaContent((prev) => {
                    if (!prev?.platforms) return prev;
                    return {
                        ...prev,
                        platforms: prev.platforms.map((p) =>
                            p.platformId === platform.id ? { ...p, content: newContent } : p
                        ),
                        threadId: response.thread_id || prev.threadId,
                        assistantId: response.assistant_id || prev.assistantId,
                    };
                });
                setInputContent("");
                toast.success('Content enhanced successfully!');
                // Also save the enhanced content to DB so it persists on reload
                try {
                    await jobService.updateMediaContent(jobId, platform.id.toLowerCase(), newContent);
                } catch (saveErr) {
                    console.warn('Auto-save after enhance failed:', saveErr);
                }
                // Refresh from server to guarantee UI is in sync
                if (fetchMediaContent) fetchMediaContent();
            } else {
                console.warn('[MediaPlan enhance] No content in response:', response);
                toast.error('Could not enhance content. Please try again.');
            }
        } catch (error) {
            console.error("Failed to enhance content:", error);
            toast.error(error?.response?.data?.message || 'Failed to enhance content. Please try again.');
        } finally {
            setIsUpdating(false);
        }
    };

    const handleSave = async () => {
        setIsSaving(true);
        await onSave(platform.id.toLowerCase(), displayContent); // Save the currently displayed content
        setIsSaving(false);
    };

    // Function to clean content by removing "Link to job" text
    const cleanContent = (text) => {
        if (typeof text !== 'string') return text;
        return text
            .replace(/Link to job posting/gi, '')
            .replace(/Apply now:/gi, '')
            .replace(/\[Link to job posting\]/gi, '')
            .trim();
    };

    const copyButton = (onClick) => (
        <Button size="sm" variant="secondary" icon={<Icon name="clipboard-text" />} onClick={onClick}>
            Copy
        </Button>
    );
    const box = { background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: 16 };
    const label = { margin: '0 0 6px', fontSize: 'var(--fs-body-sm)', fontWeight: 600, color: 'var(--text-1)' };
    const text = { background: 'var(--surface-1)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', padding: 12, fontSize: 'var(--fs-body-sm)', color: 'var(--text-2)', whiteSpace: 'pre-wrap' };

    return (
        <Card>
            <CardHeader
                title={<span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>{icon}{platform.name} Post</span>}
            />
            <div style={{ color: 'var(--text-2)' }}>
                  {(() => {
                    // Handle multiple options format
                    if (displayContent && typeof displayContent === 'object' && displayContent.options && Array.isArray(displayContent.options)) {
                      return (
                        <div style={{ display: 'grid', gap: 20 }}>
                          {/* Tab Navigation */}
                          <Tabs
                            items={displayContent.options.map((option, index) => ({ value: index, label: option.title || `Option ${index + 1}` }))}
                            value={activeTab}
                            onChange={setActiveTab}
                          />

                          {/* Tab Content */}
                          {displayContent.options.map((option, index) => (
                            <div
                              key={option.id || index}
                              style={{ display: activeTab === index ? 'block' : 'none' }}
                            >
                              <div style={box}>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 12 }}>
                                  <h4 style={{ margin: 0, fontSize: 'var(--fs-body-lg)', fontWeight: 600, color: 'var(--text-1)' }}>
                                    {option.title || `Option ${index + 1}`}
                                  </h4>
                                  {copyButton(() => {
                                      const cleanText = cleanContent(option.content);
                                      const textToCopy = `${option.title}\n\n${cleanText}\n\nHashtags: ${option.suggestedHashtags?.join(' ') || ''}\n\nKeywords: ${option.suggestedKeywords?.join(', ') || ''}`;
                                      navigator.clipboard.writeText(textToCopy);
                                      toast.success('Content copied to clipboard!');
                                  })}
                                </div>

                                <div style={{ display: 'grid', gap: 12 }}>
                                  <div>
                                    <h5 style={label}>Content:</h5>
                                    <div style={text}>
                                      {cleanContent(option.content)}
                                    </div>
                                  </div>

                                  {option.suggestedHashtags && option.suggestedHashtags.length > 0 && (
                                    <div>
                                      <h5 style={label}>Suggested Hashtags:</h5>
                                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                        {option.suggestedHashtags.map((hashtag, idx) => (
                                          <StatusPill key={idx} tone="info" dot={false}>{hashtag}</StatusPill>
                                        ))}
                                      </div>
                                    </div>
                                  )}

                                  {option.suggestedKeywords && option.suggestedKeywords.length > 0 && (
                                    <div>
                                      <h5 style={label}>Suggested Keywords:</h5>
                                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                        {option.suggestedKeywords.map((keyword, idx) => (
                                          <StatusPill key={idx} tone="success" dot={false}>{keyword}</StatusPill>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      );
                    }

                    // Handle single content format
                    if (typeof displayContent === 'string') {
                      return (
                        <div style={box}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 8 }}>
                            <h5 style={{ ...label, margin: 0 }}>Content:</h5>
                            {copyButton(() => {
                                const cleanText = cleanContent(displayContent);
                                navigator.clipboard.writeText(cleanText);
                                toast.success('Content copied to clipboard!');
                            })}
                          </div>
                          <div style={text}>
                            {cleanContent(displayContent)}
                          </div>
                        </div>
                      );
                    }

                    // Handle object format (fallback)
                    if (typeof displayContent === 'object') {
                      return (
                        <div style={box}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 8 }}>
                            <h5 style={{ ...label, margin: 0 }}>Content:</h5>
                            {copyButton(() => {
                                const cleanText = cleanContent(JSON.stringify(displayContent, null, 2));
                                navigator.clipboard.writeText(cleanText);
                                toast.success('Content copied to clipboard!');
                            })}
                          </div>
                          <pre style={{ ...text, margin: 0, overflowX: 'auto' }}>
                            {cleanContent(JSON.stringify(displayContent, null, 2))}
                          </pre>
                        </div>
                      );
                    }

                    return <div style={{ color: 'var(--text-muted)' }}>No content available</div>;
                  })()}
            </div>

            <div style={{ marginTop: 16 }}>
                <PromptInput
                    value={inputContent}
                    onChange={setInputContent}
                    onSubmit={handleGenerate}
                    onSave={handleSave}
                    loading={isUpdating}
                    disabled={isSaving}
                    placeholder={`Enhance ${platform.name} post...`}
                    saveButtonText={isSaving ? "Saving..." : "Save"}
                />
            </div>
        </Card>
    );
};
const MediaContent = () => {
    const { id } = useParams();
    const [mediaContent, setMediaContent] = useState(null);
    const [showCreator, setShowCreator] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isGenerating, setIsGenerating] = useState(false);

    useEffect(() => {
        console.log("mediaContent", mediaContent);
    }, [mediaContent]);

    const defaultPlatform = {
        id: "default",
        name: "Other Platform",
        icon: (
            <svg width="24" height="24" style={{ color: 'var(--text-2)', flex: 'none' }} fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-1-13h2v6h-2zm0 8h2v2h-2z" />
            </svg>
        ),
    };

    const platforms = [
        {
            id: "linkedin",
            name: "LinkedIn",
            icon: (
                <svg width="24" height="24" style={{ color: 'var(--text-2)', flex: 'none' }} fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                </svg>
            ),
        },
        {
            id: "indeed",
            name: "Indeed",
            icon: (
                <svg width="24" height="24" style={{ color: 'var(--text-2)', flex: 'none' }} fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm0 22C6.486 22 2 17.514 2 12S6.486 2 12 2s10 4.486 10 10-4.486 10-10 10zm1-15h-2v6h2V7zm0 8h-2v2h2v-2z" />
                </svg>
            ),
        },
        {
            id: "upwork",
            name: "Upwork",
            icon: (
                <svg width="24" height="24" style={{ color: 'var(--text-2)', flex: 'none' }} fill="currentColor" viewBox="0 0 24 24">
                    <path d="M18.561 13.158c-1.102 0-2.135-.467-3.074-1.227l.228-1.076.008-.042c.207-1.143.849-3.06 2.839-3.06 1.492 0 2.703 1.212 2.703 2.703-.001 1.489-1.212 2.702-2.704 2.702z" />
                </svg>
            ),
        },
    ];

    const fetchMediaContent = async () => {
        try {
            setLoading(true);
            const response = await jobService.getMediaContent(id);
            setMediaContent(response.data);
        } catch (err) {
            setError("Failed to load media content");
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (id) {
            fetchMediaContent();
        }
    }, [id]);

    // useEffect(() => {
    //   console.log("isGenerating:", isGenerating);
    //   /**
    //    * This is to force fetch data, after every generation
    //    * 
    //    * line 194-196
    //    * Author: @vishwesh5544
    //    */
    //     if (!isGenerating) {
    //         console.log("isGenerating:", isGenerating);
    //         fetchMediaContent();
    //     }
    // }, [isGenerating]);

    const handleGenerateContent = async (platform, prompt) => {
        setIsGenerating(true);
        try {
            const user = JSON.parse(localStorage.getItem("user"));
            const response = await jobService.generateMediaContent(id, platform, prompt, user.id);

            console.log('Full response:', response);
            console.log('Response data:', response.data);
            console.log('Response data.data:', response.data?.data);

            // The API response structure is: { success: true, message: "...", data: { aiResponse } }
            // The actual AI response is in response.data
            if (response.data && response.data.data) {
                // The actual data is nested in response.data.data
                setMediaContent((prev) => ({
                    ...prev,
                    [`${platform}Post`]: response.data.data, // Use the nested data
                    threadId: response.data.thread_id,
                    assistantId: response.data.assistant_id,
                }));
            } else if (response.data) {
                // Fallback: use response.data directly if no nested structure
                setMediaContent((prev) => ({
                    ...prev,
                    [`${platform}Post`]: response.data,
                    threadId: response.data.thread_id,
                    assistantId: response.data.assistant_id,
                }));
            } else {
                setError("Failed to generate content: Invalid response format");
            }
        } catch (error) {
            console.error("Error generating content:", error);
            setError(error.response?.data?.message || "Failed to generate content");
        } finally {
            setIsGenerating(false);
        }
    };

    const handleContentSave = async (platform, content) => {
        try {
            await jobService.updateMediaContent(id, platform, content);
            await fetchMediaContent();
        } catch (err) {
            setError("Failed to save media content");
        }
    };

    if (loading) {
        return <Card><Loading label="Loading media posts" /></Card>;
    }

    if (error) {
        return <Note tone="danger">{error}</Note>;
    }

    return (
        <div style={{ minWidth: 0 }}>
            {showCreator ? (
                <MediaContentCreator
                    jobId={id}
                    onSave={handleContentSave}
                    onCancel={() => setShowCreator(false)}
                    initialContent={mediaContent}
                />
            ) : mediaContent ? (
                <div style={{ display: 'grid', gap: 24 }}>
                    <Card>
                        <CardHeader
                            bare
                            title="Media Posts"
                            action={
                                <Button size="sm" icon={<Icon name="plus" />} onClick={() => setShowCreator(true)}>
                                    Add Media Post
                                </Button>
                            }
                        />
                    </Card>

                    {mediaContent?.platforms?.map((platformData) => {
                        console.log("platformData", platformData);
                        const platform = platforms.find((p) => p.id === platformData.platformId) || {
                            ...defaultPlatform,
                            id: platformData.platformId,
                            name: platformData.platformId.charAt(0).toUpperCase() + platformData.platformId.slice(1),
                        };
                        console.log("platform", platform);
                        // if platform not found then add one default object

                        if (!platform) return null;
                        return (
                            <SocialMediaPost
                                key={platformData.platformId}
                                platform={platform}
                                icon={platform.icon}
                                content={platformData.content}
                                isGenerating={isGenerating}
                                onGenerate={handleGenerateContent}
                                onSave={handleContentSave}
                                threadId={mediaContent.threadId}
                                assistantId={mediaContent.assistantId}
                                setMediaContent={setMediaContent}
                            />
                        );
                    })}
                </div>
            ) : (
                <BlankData title="Media Plan Content" onAddClick={() => setShowCreator(true)} />
            )}
        </div>
    );
};

export default MediaContent;
